import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../AppContext';
import { formatDate } from '../types';

interface SuccessSummary {
  service: string;
  dateLabel: string;
  time: string;
  name: string;
}

export default function BookingWizard() {
  const {
    services, availability,
    addAppointment, showToast,
    preselectedService, consumePreselect,
    goTo,
  } = useApp();

  const [step, setStep] = useState(1);
  const [service, setService] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [success, setSuccess] = useState<SuccessSummary | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [stepBouncing, setStepBouncing] = useState(false);

  useEffect(() => {
    if (!service && services.length > 0) setService(services[0].name);
  }, [services, service]);

  useEffect(() => {
    if (preselectedService) {
      setService(preselectedService);
      setStep(2);
      consumePreselect();
    }
  }, [preselectedService, consumePreselect]);

  useEffect(() => {
    setStepBouncing(false);
    const t = setTimeout(() => setStepBouncing(true), 0);
    return () => clearTimeout(t);
  }, [step]);

  // Solo giorni aperti con almeno un'ora libera
  const openDays = useMemo(
    () => availability.filter(d => d.is_open && d.hours.some(h => !d.occupied.includes(h))),
    [availability],
  );

  const selectedDayInfo = useMemo(
    () => availability.find(d => d.date === selectedDate),
    [availability, selectedDate],
  );

  const titles: Record<number, string> = {
    1: 'Scegli il Servizio',
    2: 'Giorno & Ora',
    3: 'I tuoi Dettagli',
  };

  const next = async () => {
    if (submitting) return;
    if (step === 1) {
      if (!service) return;
      setStep(2);
    } else if (step === 2) {
      if (!selectedDate || !selectedTime) {
        showToast('Seleziona sia un giorno che una fascia oraria prima di procedere.');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      const n = name.trim();
      const p = phone.trim();
      const e = email.trim();
      if (!n || !p || !e) {
        showToast('Compila tutti i campi obbligatori contrassegnati da *');
        return;
      }
      if (!agreed) {
        showToast('È necessario accettare il trattamento dei dati personali.');
        return;
      }
      setSubmitting(true);
      try {
        await addAppointment({
          client_name: n, phone: p, email: e,
          service_name: service,
          date: selectedDate, time: selectedTime,
          notes: notes.trim() || 'Nessuna nota aggiuntiva.',
        });
        setSuccess({
          service,
          dateLabel: formatDate(selectedDate),
          time: selectedTime,
          name: n,
        });
        showToast(`Nuova richiesta di prenotazione ricevuta da ${n}!`);
      } catch (err: any) {
        showToast(`Errore invio prenotazione: ${err?.message || 'riprova più tardi.'}`);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const prev = () => { if (step > 1 && !submitting) setStep(step - 1); };

  const resetFlow = () => {
    setStep(1);
    setSelectedDate('');
    setSelectedTime('');
    setName(''); setPhone(''); setEmail(''); setNotes('');
    setAgreed(false);
    setSuccess(null);
  };

  const orderedServices = [...services].sort((a, b) => a.order - b.order);

  return (
    <section id="prenota" className="py-24 bg-cream">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Sidebar */}
          <div className="reveal lg:col-span-5 space-y-8 lg:sticky lg:top-28">
            <div className="space-y-4">
              <div className="flex items-baseline gap-4">
                <span className="font-serif text-5xl italic text-sage/70 leading-none">04</span>
                <span className="text-xs uppercase tracking-[0.3em] text-clay font-semibold">Prenota</span>
              </div>
              <h2 className="font-serif text-4xl text-forestDark leading-[1.1]">Il primo colloquio <em className="italic font-light text-sage">è gratuito</em>.</h2>
              <p className="text-sm text-charcoal/80 leading-relaxed">
                Uno spazio per conoscerci, chiarire dubbi e valutare insieme se iniziare un percorso terapeutico. Puoi prenotare dal sito o contattarmi su WhatsApp.
              </p>
            </div>

            <div className="space-y-4 pt-4 border-t border-forest/10">
              <ContactRow
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 00.096.837l-1.997 1.997a14.12 14.12 0 005.337 5.337l1.997-1.997a1 1 0 01.837-.096l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>}
                label="Telefono / WhatsApp" value="+39 347 2208426"
              />
              <ContactRow
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
                label="Email" value="m.balducci1987@gmail.com"
              />
              <ContactRow
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>}
                label="Modalità" value="Online — Google Meet"
              />
            </div>
          </div>

          {/* Wizard */}
          <div className="reveal lg:col-span-7 bg-beigeBG p-8 md:p-10 rounded-3xl border border-forest/10 shadow-md">
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-forest/5">
                <div className="flex items-center space-x-2">
                  <span className={`w-6 h-6 rounded-full bg-forest text-cream text-xs flex items-center justify-center font-bold smooth-transition ${stepBouncing ? 'step-bounce' : ''}`}>{step}</span>
                  <span className="text-xs uppercase tracking-wider font-bold text-forest">{titles[step]}</span>
                </div>
                <span className="text-xs text-charcoal/50">Passo {step} di 3</span>
              </div>

              {success && (
                <div className="step-anim text-center py-8 space-y-6">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto text-emerald-800 animate-result-pop">
                    <svg className="w-8 h-8 check-draw" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-serif text-2xl text-forestDark">Richiesta di prenotazione inviata!</h3>
                    <p className="text-sm text-charcoal/70 max-w-sm mx-auto">
                      Grazie per la fiducia. Il Dr. Balducci verificherà lo slot orario e ti contatterà via email/telefono entro 12 ore per la conferma definitiva.
                    </p>
                  </div>
                  <div className="p-4 bg-cream rounded-2xl border border-emerald-800/10 text-xs text-left max-w-md mx-auto space-y-1.5">
                    <div><span className="font-bold">Servizio:</span> {success.service}</div>
                    <div><span className="font-bold">Giorno:</span> {success.dateLabel}</div>
                    <div><span className="font-bold">Ora:</span> {success.time}</div>
                    <div><span className="font-bold">Nominativo:</span> {success.name}</div>
                  </div>
                  <button onClick={resetFlow} className="text-xs font-bold uppercase tracking-wider text-sage hover:text-forest smooth-transition">
                    Prenota un altro colloquio
                  </button>
                </div>
              )}

              {!success && step === 1 && (
                <div className="step-anim space-y-4">
                  <h3 className="font-serif text-lg text-forestDark">Seleziona il servizio desiderato:</h3>
                  {orderedServices.length === 0 && <p className="text-xs text-charcoal/50 italic">Caricamento servizi…</p>}
                  <div className="grid grid-cols-1 gap-3">
                    {orderedServices.map(s => (
                      <label key={s.id} className="booking-option flex items-center justify-between p-4 bg-cream rounded-xl border border-forest/5 cursor-pointer hover:border-forest/30 smooth-transition">
                        <div className="flex items-center space-x-3">
                          <input type="radio" name="booking-service" value={s.name} checked={service === s.name} onChange={() => setService(s.name)} className="text-forest focus:ring-forest" />
                          <span className="font-medium text-sm text-forestDark">{s.name}</span>
                        </div>
                        <span className="text-xs font-semibold text-sage">€{s.price_eur} / {s.duration_min} min</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {!success && step === 2 && (
                <div className="step-anim space-y-4">
                  <h3 className="font-serif text-lg text-forestDark">Seleziona giorno e ora disponibili:</h3>

                  <div className="space-y-2">
                    <span className="text-xs text-charcoal/60 uppercase tracking-wider font-semibold">Prossimi giorni disponibili</span>
                    {openDays.length === 0 ? (
                      <div className="text-center py-6 text-xs text-charcoal/50 italic">
                        Nessuna disponibilità nelle prossime 6 settimane. Contatta direttamente lo studio.
                      </div>
                    ) : (
                      <div className="overflow-x-auto -mx-2 px-2">
                        <div className="flex gap-2 pb-2" style={{ minWidth: 'min-content' }}>
                          {openDays.slice(0, 28).map(d => {
                            const selected = selectedDate === d.date;
                            const dateObj = new Date(d.date + 'T00:00:00');
                            return (
                              <button
                                key={d.date}
                                type="button"
                                onClick={() => { setSelectedDate(d.date); setSelectedTime(''); }}
                                className={`shrink-0 w-20 p-3 rounded-xl border text-center smooth-transition hover:-translate-y-0.5 ${selected ? 'bg-forest text-cream border-forest shadow-md' : 'bg-cream text-forestDark border-forest/10 hover:border-forest/40'}`}
                              >
                                <span className="block text-[10px] font-bold uppercase tracking-wider">{d.weekday_name.slice(0, 3)}</span>
                                <span className="block text-xl font-serif font-semibold leading-tight">{dateObj.getDate()}</span>
                                <span className={`block text-[10px] ${selected ? 'text-cream/70' : 'text-charcoal/50'}`}>
                                  {dateObj.toLocaleDateString('it-IT', { month: 'short' })}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 pt-2">
                    <span className="text-xs text-charcoal/60 uppercase tracking-wider font-semibold">Fasce Orarie Disponibili</span>
                    <div className="grid grid-cols-3 gap-2">
                      {!selectedDayInfo && (
                        <div className="col-span-3 text-center py-4 text-xs text-charcoal/50 italic">
                          Scegli prima il giorno per vedere le ore disponibili
                        </div>
                      )}
                      {selectedDayInfo && selectedDayInfo.hours.map((hour, i) => {
                        const occupied = selectedDayInfo.occupied.includes(hour);
                        const selected = selectedTime === hour;
                        return (
                          <button
                            key={hour}
                            type="button"
                            disabled={occupied}
                            onClick={() => !occupied && setSelectedTime(hour)}
                            style={{ ['--d' as any]: `${i * 40}ms` }}
                            className={`slot-anim p-2.5 rounded-xl border text-sm text-center smooth-transition hover:-translate-y-0.5 ${
                              occupied
                                ? 'bg-stone-100 text-stone-400 border-stone-200 text-xs cursor-not-allowed line-through hover:translate-y-0'
                                : selected
                                  ? 'bg-sage text-forestDark border-sage shadow-md'
                                  : 'bg-cream text-forestDark border-forest/10 hover:border-forest/40'
                            }`}
                          >
                            {occupied ? `${hour} (Occupato)` : hour}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {!success && step === 3 && (
                <div className="step-anim space-y-4">
                  <h3 className="font-serif text-lg text-forestDark">I tuoi contatti per la conferma:</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-charcoal/70">Nome e Cognome *</label>
                      <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Esempio: Mario Rossi" className="w-full bg-cream border border-forest/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 smooth-transition" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-charcoal/70">Numero di Telefono *</label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Esempio: 333 1234567" className="w-full bg-cream border border-forest/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 smooth-transition" />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-charcoal/70">Indirizzo Email *</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Esempio: mario.rossi@email.com" className="w-full bg-cream border border-forest/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 smooth-transition" />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-charcoal/70">Note Aggiuntive (Facoltativo)</label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Scrivi brevemente le tue esigenze o se preferisci la modalità Online o Studio." className="w-full bg-cream border border-forest/15 rounded-xl p-4 text-sm focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 smooth-transition" />
                  </div>

                  <label className="flex items-start space-x-2.5 pt-2 cursor-pointer">
                    <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 text-forest focus:ring-forest" />
                    <span className="text-xs text-charcoal/75">
                      Consento al trattamento dei dati personali ai fini organizzativi secondo le normative vigenti, dopo aver letto la{' '}
                      <button type="button" onClick={(e) => { e.preventDefault(); goTo('privacy'); }} className="text-forest underline hover:text-forestDark">
                        Privacy Policy
                      </button>. *
                    </span>
                  </label>
                </div>
              )}

              {!success && (
                <div className="flex items-center justify-between pt-6 border-t border-forest/5">
                  <button
                    onClick={prev}
                    className={`text-xs uppercase tracking-widest font-bold text-sage hover:text-forest smooth-transition ${step === 1 ? 'invisible' : ''}`}
                  >
                    ← Indietro
                  </button>
                  <button
                    onClick={next}
                    disabled={submitting}
                    className="btn-shine bg-forest hover:bg-forestDark text-cream px-8 py-3 rounded-full text-sm font-semibold smooth-transition hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {submitting ? 'Invio…' : step === 3 ? 'Richiedi Appuntamento' : 'Continua →'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="contact-row flex items-center space-x-4 smooth-transition">
      <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center text-forest shrink-0 contact-icon smooth-transition">
        {icon}
      </div>
      <div>
        <span className="block text-xs text-charcoal/60">{label}</span>
        <span className="font-medium text-forestDark">{value}</span>
      </div>
    </div>
  );
}
